import axios from '../../util/Axios';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Fab, Grid, Paper, Tab, Tabs } from '@mui/material';
import { Box } from '@mui/system';
import { debounce } from 'lodash';
import { Annotation } from './Annotation';
import { Comment } from './Comment';
import { GeneralDetails } from './GeneralDetails';
import CloseIcon from '@mui/icons-material/Close';

import { Video } from './Video';
import { SectionHeader } from './SectionHeader';
import { NeurologicalExamination } from './NeurologicalExamination/NeurologicalExamination';
import { ImagingResults } from './ImagingResults';
import { AttachmentList } from '../Attachments/AttachmentList';
import { ClinicianComment } from './ClinicianComment';
import { EvaluationResults } from './AIResult/EvaluationResults';
import { AnnotationComplete } from './AnnotationComplete';
import { Audio } from './Audio';
import { TabPanel, a11yProps } from '../TabPanel';
import { Gaze } from './Gaze';
import { ArmsCombined } from './ArmsCombined';
import { NeglectAssessment } from './OnDeviceResult/NeglectAssessment';
import { useArmsData } from '../../hooks/useArmsData';

const TestDetails = forwardRef((props, ref) => {

  const {id, onClose, showCloseButton = true, inDialog = true} = props;
  const testId = id;

  const { armsData } = useArmsData(testId);

  const [test, setTest] = useState(null);
  const [speechAssessment, setSpeechAssessment] = useState(null);
  const [video, setVideo] = useState([]);

  const [value, setValue] = useState(0);

  const paperSyle = inDialog
    ? {
        position: 'absolute',
        width: '100%',
        minWidth: '700px',
        height: '100%',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
      }
    : {};

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

   useEffect(() => {
    
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        const resp = await axios(`/strokeTests/${testId}/details`, { signal });
        const { data } = resp;
        setTest(data);
        setSpeechAssessment({
          dysarthriaResponse: data.dysarthriaResponse,
          dysarthriaDetected: data.dysarthriaDetected,
          aphasiaResponse: data.aphasiaResponse,
          aphasiaDetected: data.aphasiaDetected,
          aphasiaVideoUrl: data.aphasiaVideoUrl,
          testId: testId 
        });

        if (data.videoUrl) {
          const resp = await axios(`/strokeTests/${testId}/video`, { responseType: 'blob', signal });
          setVideo(window.URL.createObjectURL(resp.data));
        } else {
          setVideo(null);
        }
      } catch (error) {
        if (error.message === 'CANCELLED') return;
        console.log(error)
      }
    };

    fetchData();

    return () => controller.abort();
  }, [testId]);

  const changeTestStatus = (status) => {
    axios('strokeTests/' + testId + '/status', {
      method: 'PUT',
      data: {
        status: status,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };

  const changeFaceStatus = (status) => {
    axios('strokeTests/' + testId + '/faceStatus', {
      method: 'PUT',
      data: {
        status: status,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };

  const changeSpeechStatus = (status) => {
    axios('strokeTests/' + testId + '/speechStatus', {
      method: 'PUT',
      data: {
        status: status,
      },
    }).then((resp) =>
      axios('/strokeTests/' + testId + '/details').then((resp) => {
        setTest(resp.data);
      })
    );
  };

  const changeArmsStatus = (status) => {
    axios('strokeTests/' + testId + '/armsStatus', {
      method: 'PUT',
      data: {
        status: status,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };

  const changeNeurologicalExamniationStatus = (status) => {
    axios('strokeTests/' + testId + '/neurologicalExaminationStatus', {
      method: 'PUT',
      data: {
        status: status,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };

  const changeReviewComplete = (value) => {
    axios('strokeTests/' + testId + '/reviewComplete', {
      method: 'PUT',
      data: {
        value: value,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };

  const changeTestCommentHandler = (comment) => {
    axios('strokeTests/' + testId + '/comment', {
      method: 'PUT',
      data: {
        comment: comment,
      },
    }).then((resp) => axios('/strokeTests/' + testId + '/details').then((resp) => setTest(resp.data)));
  };
  // eslint-disable-next-line
  const changeTestComment = useCallback(debounce(changeTestCommentHandler, 1000), []);

  return (
    <div ref={ref} tabIndex={-1}>
      {showCloseButton && (
        <Fab size="medium" color="primary" aria-label="add" sx={{ margin: '8px', position: 'absolute', right: 0 }} onClick={onClose}>
          <CloseIcon />
        </Fab>
      )}
      <Paper sx={paperSyle}>
        {test && (
          <>
            <Grid container>
              <Grid item xs={8} sx={{ padding: '8px' }}>
                <Tabs value={value} onChange={handleChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label="Video" {...a11yProps(0)} />
                  <Tab label="Speech" {...a11yProps(0)} />
                  <Tab label="Arms" {...a11yProps(1)} />
                  <Tab label="Gaze" {...a11yProps(1)} />
                  <Tab label="Neglect" {...a11yProps(1)} />
                  <Tab label="Files" {...a11yProps(1)} />
                  <Tab label="AI Score" {...a11yProps(1)} />
                </Tabs>
                <TabPanel value={value} index={0}>
                  <Video video={video} model1Result={test.model1Result} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                  <Audio
                    model1Result={test.model1Result}
                    speechAssessment={speechAssessment}
                    test={test}
                  />
                </TabPanel>
                <TabPanel value={value} index={2}>
                  <ArmsCombined armsData={armsData} model1Result={test.model1Result} deviceDescription={test.deviceDescription} test={test}/>
                </TabPanel>
                <TabPanel value={value} index={3}>
                  <Gaze testDetails={test} />
                </TabPanel>
                <TabPanel value={value} index={4}>
                  <NeglectAssessment testDetails={test} />
                </TabPanel>
                <TabPanel value={value} index={5}>
                  <AttachmentList testId={test.uuid} attachments={test.attachments} />
                </TabPanel>
                <TabPanel value={value} index={6}>
                  <EvaluationResults testId={test.uuid} model1Result={test.model1Result} />
                </TabPanel>
              </Grid>

              <Grid item xs={4} sx={{ backgroundColor: 'grey.50' }}>
                <Box
                  sx={{
                    m: 1,
                    height: '750px',
                    overflowX: 'scroll',
                    overflowY: 'scroll',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                  }}
                >
                  <Grid container spacing={2}>
                    <GeneralDetails test={test} />
                    <NeurologicalExamination test={test} />
                    <ImagingResults test={test} />
                    <ClinicianComment test={test} />
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <SectionHeader name="Annotation" />
                      </Grid>
                      {test.reviewComplete ? (
                        <AnnotationComplete test={test} changeReviewComplete={changeReviewComplete} />
                      ) : (
                        <Annotation
                          test={test}
                          changeTestStatus={changeTestStatus}
                          changeFaceStatus={changeFaceStatus}
                          changeSpeechStatus={changeSpeechStatus}
                          changeArmsStatus={changeArmsStatus}
                          changeNeurologicalExaminationStatus={changeNeurologicalExamniationStatus}
                          changeReviewComplete={changeReviewComplete}
                        />
                      )}

                      <Comment comment={test.comment} saveComment={changeTestComment} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </div>
  );
});

export default TestDetails;
